import { useState, useEffect } from "react";

/**
 * Position a PORTALED dropdown against its trigger (SOUND-616/617).
 *
 * Why portals are needed at all: `AccountLayout` wraps the top bar in
 * `sticky … z-20`, which creates a stacking context. Any dropdown rendered as a
 * child of the top bar is trapped inside it, and `PageLayout`'s sticky header
 * (`z-30` + `backdrop-blur`) is a SIBLING of that wrapper — so it paints over the
 * dropdown on every page with a sticky header. Raising a z-index only moves the
 * problem; SOUND-239 fixed the same class of bug once already. Portaling to
 * `document.body` escapes every stacking context permanently.
 *
 * The cost of a portal is that `absolute right-0` no longer anchors to anything,
 * which is what this hook restores: viewport coordinates for `position: fixed`,
 * right-aligned to the trigger the way the original `right-0` was.
 *
 * Returns null until measured, so callers should render nothing until it's set.
 *
 * @param {boolean} open - whether the dropdown is showing
 * @param {{current: HTMLElement|null}} triggerRef - the trigger element
 * @returns {{top: number, right: number}|null} viewport px for top / right
 */
export function useAnchorRect(open, triggerRef) {
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      // `right` is the distance from the viewport's right edge to the trigger's
      // right edge, so a fixed element with that `right` stays right-aligned to
      // the trigger regardless of the panel's own width.
      if (r) setAnchor({ top: r.bottom + 8, right: window.innerWidth - r.right });
    };
    place();
    // The trigger lives in a sticky top bar, so it never scrolls away — but a
    // resize moves it, and a stale anchor would leave the panel detached.
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, triggerRef]);

  return anchor;
}

export default useAnchorRect;
